import React, { useMemo } from 'react';
import { formatCurrency } from '../lib/utils';
import { Checkbox } from './ui/checkbox';
import { CheckCircle, AlertTriangle } from 'lucide-react';

export const EditionBulkBookingsTable = ({ bookings, selectedIds, onToggle, onToggleAll, advertisersById = {} }) => {
  // Group by advertiser_id
  const grouped = useMemo(() => {
    const map = new Map();
    for (const bk of bookings) {
      const key = bk.advertiser_id;
      if (!map.has(key)) map.set(key, { advertiser_name: bk.advertiser_name || 'Unknown', items: [] });
      map.get(key).items.push(bk);
    }
    return [...map.values()].sort((a, b) => (a.advertiser_name || '').localeCompare(b.advertiser_name || ''));
  }, [bookings]);

  if (bookings.length === 0) {
    return (
      <div className="text-center py-8 text-[#94A3B8]" data-testid="edition-bulk-empty">
        <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p>No uninvoiced bookings for this edition</p>
        <p className="text-xs mt-1">Only confirmed and production bookings appear here</p>
      </div>
    );
  }

  return (
    <div className="border border-[#1E293B] rounded-md overflow-hidden" data-testid="edition-bulk-table">
      <table className="w-full">
        <thead>
          <tr className="bg-[#0A0A0A]/80 border-b border-[#1E293B]">
            <th className="py-2 px-3 text-left w-10">
              <Checkbox
                checked={selectedIds.length === bookings.length && bookings.length > 0}
                onCheckedChange={onToggleAll}
                data-testid="edition-bulk-select-all"
              />
            </th>
            <th className="py-2 px-3 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Client / Booking</th>
            <th className="py-2 px-3 text-left text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Product</th>
            <th className="py-2 px-3 text-right text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Rate</th>
            <th className="py-2 px-3 text-center text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Tax</th>
            <th className="py-2 px-3 text-right text-xs font-medium tracking-wide uppercase text-[#94A3B8]">Total</th>
          </tr>
        </thead>
        <tbody>
          {grouped.map(group => {
            const groupIds = group.items.map(b => b.id);
            const allSelected = groupIds.every(id => selectedIds.includes(id));
            const someSelected = !allSelected && groupIds.some(id => selectedIds.includes(id));
            const groupTotal = group.items
              .filter(b => selectedIds.includes(b.id))
              .reduce((s, b) => s + (b.rate || 0) + ((b.is_taxable ? (b.rate || 0) * (b.tax_rate || 0) / 100 : 0)), 0);
            return (
              <React.Fragment key={group.items[0].advertiser_id}>
                <tr className="bg-[#3B82F6]/10 border-t border-[#1E293B]">
                  <td className="py-1.5 px-3">
                    <Checkbox
                      checked={allSelected}
                      data-state={someSelected ? 'indeterminate' : (allSelected ? 'checked' : 'unchecked')}
                      onCheckedChange={() => {
                        if (allSelected) {
                          groupIds.forEach(id => { if (selectedIds.includes(id)) onToggle(id); });
                        } else {
                          groupIds.forEach(id => { if (!selectedIds.includes(id)) onToggle(id); });
                        }
                      }}
                      data-testid={`bulk-group-${group.items[0].advertiser_id}`}
                    />
                  </td>
                  <td colSpan={4} className="py-1.5 px-3 text-sm font-bold text-[#3B82F6] uppercase tracking-wide">
                    <span className="inline-flex items-center gap-2">
                      {group.advertiser_name}
                      {(() => {
                        const adv = advertisersById[group.items[0].advertiser_id];
                        const hasEmail = adv && (adv.email || adv.contact_email);
                        if (adv && !hasEmail) {
                          return (
                            <span title="No email on file — invoice will not be delivered" className="inline-flex items-center text-red-400 text-[10px] gap-0.5 normal-case font-normal" data-testid={`no-email-badge-${adv.id}`}>
                              <AlertTriangle className="w-3 h-3" /> no email
                            </span>
                          );
                        }
                        return null;
                      })()}
                      <span className="ml-2 text-[10px] text-[#94A3B8] font-normal lowercase">
                        ({group.items.length} booking{group.items.length !== 1 ? 's' : ''})
                      </span>
                    </span>
                  </td>
                  <td className="py-1.5 px-3 text-sm text-right font-semibold text-[#F8FAFC]">
                    {formatCurrency(groupTotal)}
                  </td>
                </tr>
                {group.items.map(bk => {
                  const tax = bk.is_taxable ? (bk.rate || 0) * (bk.tax_rate || 0) / 100 : 0;
                  return (
                    <tr key={bk.id} className="hover:bg-[#1A1A1A] border-t border-[#1E293B]/50" data-testid={`edition-bulk-row-${bk.id}`}>
                      <td className="py-2 px-3 pl-8">
                        <Checkbox
                          checked={selectedIds.includes(bk.id)}
                          onCheckedChange={() => onToggle(bk.id)}
                          data-testid={`edition-bulk-toggle-${bk.id}`}
                        />
                      </td>
                      <td className="py-2 px-3 text-sm text-[#94A3B8] pl-8" colSpan={1}>
                        <span className="text-[#475569]">↳</span>
                      </td>
                      <td className="py-2 px-3 text-sm text-[#F8FAFC]">{bk.product_type}</td>
                      <td className="py-2 px-3 text-sm text-[#F8FAFC] text-right">{formatCurrency(bk.rate)}</td>
                      <td className="py-2 px-3 text-sm text-[#94A3B8] text-center">
                        {bk.is_taxable ? `${bk.tax_rate}%` : '-'}
                      </td>
                      <td className="py-2 px-3 text-sm text-[#F8FAFC] text-right font-medium">{formatCurrency((bk.rate || 0) + tax)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
